import { getDynamicQuery } from '../../application/query';
import { DEFAULT_SEARCH_LIMIT, ADMIN_SEARCH_INDEX, DATE_PROPERTY } from '../../constants';

export default function getAdminRecordSearch(schema, client) {
  const { query, getVariables } = setupQuery(schema);
  return async ({ event: { value: searchedValue }, currentValue, currentOffset: offset = 0 }) => {
    if (offset && offset % DEFAULT_SEARCH_LIMIT) return null;

    const { data: { searchData, searchResults: { results = [] } = {} } = {} } = await client.query({
      query,
      variables: getVariables(searchedValue, currentValue, offset),
      fetchPolicy: 'network-only'
    });
    return searchData || results;
  };
}

const getIdentifierAndIndexes = (schema, hasMultipleSchema) => {
  if (hasMultipleSchema) {
    let allIndexes = [];
    const identiis = schema.map(({ id, extras: { searchIndexes: extraIndexes } }) => {
      if (extraIndexes) allIndexes = [...allIndexes, ...extraIndexes];
      return id;
    });
    const uniqueIndexes = [...new Set(allIndexes)];
    return [identiis, uniqueIndexes];
  }
  const { id: entityIdentifier, extras: { searchIndexes = [] } = {} } = schema;
  return [entityIdentifier, searchIndexes];
};

function getCustomIndexName(schemas) {
  const indexes = schemas
    .map(schema => {
      const { displayProperties: { adminListings: { dataSource } = {} } = {} } = schema;
      if (dataSource && dataSource.source === 'search' && dataSource.index) {
        return dataSource.index;
      }
      return null;
    })
    .filter(index => index !== null);

  if (indexes.length === 0) {
    return null;
  }

  const firstIndex = indexes[0];
  const allSame = indexes.every(index => index === firstIndex);
  return allSame ? firstIndex : null;
}

function setupQuery(schema) {
  const hasMultipleSchema = Array.isArray(schema);
  const [entityIdentifier, searchIndexes] = getIdentifierAndIndexes(schema, hasMultipleSchema);
  const schemaForAdmin = hasMultipleSchema ? schema : [schema];

  const entityInAdminIndex = searchIndexes.includes(ADMIN_SEARCH_INDEX);
  let indexName = null;
  if (entityInAdminIndex) {
    indexName = 'admin';
  } else {
    indexName = getCustomIndexName(schemaForAdmin);
  }

  const useSearchIndex = !!indexName;

  const query = useSearchIndex
    ? getDynamicQuery('ADMIN_SEARCH')(schemaForAdmin, null, false, indexName)
    : getDynamicQuery('RELATION_SEARCH_QUERY')(schema);

  const getVariables = (searchedValue, currentValue, offset) => {
    const { properties = {}, dynamicProperies = {} } = schema;

    const isSearchableByDate =
      (properties[DATE_PROPERTY] && properties[DATE_PROPERTY].searchable) ||
      (dynamicProperies[DATE_PROPERTY] && dynamicProperies[DATE_PROPERTY].searchable);

    const where = useSearchIndex
      ? getSearchIndexVariables({
          searchedValue,
          currentValue,
          entityIdentifier,
          hasMultipleSchema,
          isSearchableByDate
        })
      : getStandardWhere(searchedValue, currentValue);

    return {
      where,
      limit: DEFAULT_SEARCH_LIMIT,
      offset
    };
  };

  return { query, getVariables };
}

function getStandardWhere(searchedValue, currentValue) {
  return { name: { _ilike: `%${searchedValue}%` }, id: { _nin: currentValue || [] } };
}

const getShouldValues = (entityIdentifier, hasMultipleSchema) => {
  if (hasMultipleSchema) {
    return entityIdentifier.map(identifier => ({
      match: {
        docType: identifier
      }
    }));
  }
  return [
    {
      match: {
        docType: entityIdentifier
      }
    }
  ];
};

function escapeSimpleQueryString(value) {
  return value.replace(/([+\-=!(){}[\]^"~*?:\\/&|<>])/g, '\\$1');
}

function getSearchIndexVariables({
  searchedValue,
  currentValue,
  entityIdentifier,
  hasMultipleSchema,
  isSearchableByDate
}) {
  const normalizedValue = escapeSimpleQueryString(searchedValue.toLowerCase());
  let searchQuery = {
    bool: {
      must: [
        {
          bool: {
            should: [
              {
                simple_query_string: {
                  query: normalizedValue,
                  flags: 'PHRASE'
                }
              },
              {
                simple_query_string: {
                  query: `${normalizedValue}*`,
                  flags: 'PREFIX'
                }
              }
            ],
            minimum_should_match: 1
          }
        }
      ],
      should: getShouldValues(entityIdentifier, hasMultipleSchema),
      minimum_should_match: 1,
      must_not: [
        {
          ids: {
            values: currentValue || [] // todo: force returning current value
          }
        }
      ]
    }
  };

  if (isSearchableByDate) {
    searchQuery = {
      function_score: {
        query: searchQuery,
        functions: [
          {
            gauss: {
              [DATE_PROPERTY]: {
                origin: 'now',
                scale: '30d',
                offset: '7d',
                decay: 0.5
              }
            }
          }
        ],
        boost_mode: 'multiply'
      }
    };
  }
  return JSON.stringify(searchQuery);
}
